import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Checkbox } from "../../../app/components/ui/checkbox";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../../../app/components/ui/drawer";
import { Input } from "../../../app/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { Skeleton } from "../../../app/components/ui/skeleton";
import { BottomActionBar } from "../../../components/ui/BottomActionBar";
import { api } from "../../../lib/api";
import { buildCsvFromRows, downloadCsvFile } from "../../../lib/csv";
import { useIsMobile } from "../../../lib/useBreakpoint";
import { useAuth } from "../../../providers/AuthProvider";
import { useDealers } from "../../dealers/hooks/useDealers";
import { useProducts } from "../../products/hooks/useProducts";
import { useInventoryEntries } from "../hooks/useInventoryEntries";

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

const PER_PAGE = 15;

export function InventoryEntriesPage() {
  const { allEntries, loading, error } = useInventoryEntries();
  const { token } = useAuth();
  const { dealers } = useDealers();
  const { products } = useProducts();
  const isMobile = useIsMobile();
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [mobileDrawerEntryId, setMobileDrawerEntryId] = useState<string | null>(null);
  const [details, setDetails] = useState<
    Record<
      string,
      | {
          id: string;
          business_id: string;
          dealer_id: string;
          reference_no: string;
          total_amount: string;
          items: Array<{
            product_id: string;
            quantity: string;
            purchase_price: string;
            selling_price: string;
            mrp: string;
          }>;
          created_at: string | null;
        }
      | null
    >
  >({});
  const [loadingDetailId, setLoadingDetailId] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const dealerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of dealers) {
      map.set(String(d.id), d.name);
    }
    return map;
  }, [dealers]);

  const productNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of products) {
      map.set(String(p.id), p.name);
    }
    return map;
  }, [products]);

  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allEntries;
    return allEntries.filter((e) => {
      const dealer = dealerNameById.get(String(e.dealer_id)) ?? String(e.dealer_id);
      return (
        String(e.id).toLowerCase().includes(q) ||
        e.reference_no.toLowerCase().includes(q) ||
        dealer.toLowerCase().includes(q) ||
        (e.batch_ids?.join(" ") ?? "").toLowerCase().includes(q)
      );
    });
  }, [allEntries, searchQuery, dealerNameById]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / PER_PAGE));
  const pageClamped = Math.min(page, totalPages);
  const entries = useMemo(() => {
    const start = (pageClamped - 1) * PER_PAGE;
    return filteredEntries.slice(start, start + PER_PAGE);
  }, [filteredEntries, pageClamped]);

  const selectedCount = selectedIds.size;
  const allSelected =
    entries.length > 0 && entries.every((e) => selectedIds.has(String(e.id)));
  const someSelected = entries.some((e) => selectedIds.has(String(e.id)));

  const toggleRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const e of entries) {
          if (checked) next.add(String(e.id));
          else next.delete(String(e.id));
        }
        return next;
      });
    },
    [entries],
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const rowsToCsvData = useCallback(
    (list: typeof allEntries) => {
      const header = [
        "entry_id",
        "dealer_id",
        "dealer_name",
        "batch_ids",
        "reference_no",
        "total_amount",
        "created_at",
      ];
      const rows = list.map((e) => [
        String(e.id),
        String(e.dealer_id),
        dealerNameById.get(String(e.dealer_id)) ?? "",
        e.batch_ids?.length ? e.batch_ids.join(";") : "",
        e.reference_no,
        e.total_amount,
        e.created_at ?? "",
      ]);
      return buildCsvFromRows(header, rows);
    },
    [dealerNameById],
  );

  const exportAll = () => {
    if (allEntries.length === 0) return;
    downloadCsvFile(`inventory-entries-export-all-${stamp()}.csv`, rowsToCsvData(allEntries));
  };

  const exportSelected = () => {
    const list = allEntries.filter((e) => selectedIds.has(String(e.id)));
    if (list.length === 0) return;
    downloadCsvFile(`inventory-entries-export-selected-${stamp()}.csv`, rowsToCsvData(list));
  };

  async function loadDetail(id: string) {
    setDetailError(null);
    if (!token) return;
    if (details[id]) return;
    try {
      setLoadingDetailId(id);
      const res = await api.inventory.show({ token, id });
      setDetails((prev) => ({ ...prev, [id]: res }));
    } catch (e) {
      const err = e as Error;
      setDetailError(err.message || "Failed to load entry details.");
    } finally {
      setLoadingDetailId(null);
    }
  }

  async function toggleExpand(id: string) {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    await loadDetail(id);
  }

  async function openMobileDrawer(id: string) {
    setMobileDrawerEntryId(id);
    await loadDetail(id);
  }

  const mobileDrawerEntry = mobileDrawerEntryId
    ? allEntries.find((e) => String(e.id) === mobileDrawerEntryId)
    : null;

  return (
    <div className="p-4 space-y-6 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground mb-1 md:text-3xl">Inventory entries</h1>
          <p className="text-muted-foreground">Record purchases and stock intake.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button type="button" variant="outline" onClick={exportAll} disabled={loading || allEntries.length === 0}>
            Export all
          </Button>
          <Button asChild variant="outline">
            <Link to="/inventory/invoice">Invoice upload</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/inventory/bulk">Bulk upload</Link>
          </Button>
          <Button asChild>
            <Link to="/inventory/new">Create entry</Link>
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>All entries</CardTitle>
          <CardDescription>Latest inventory intake entries</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? <div className="text-sm text-destructive">{error}</div> : null}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Input
              type="search"
              placeholder="Search by ID, dealer, reference, batches…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md min-h-9"
              aria-label="Filter entries"
            />
            <div className="text-sm text-muted-foreground">
              {filteredEntries.length} entr{filteredEntries.length === 1 ? "y" : "ies"}
              {searchQuery.trim() ? " (filtered)" : ""}
            </div>
          </div>

          {selectedCount > 0 && !isMobile ? (
            <div className="flex flex-wrap items-center gap-2 p-3 border rounded-md bg-muted/30">
              <span className="text-sm font-medium">{selectedCount} selected</span>
              <Button type="button" size="sm" variant="outline" onClick={clearSelection}>
                Clear selection
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={exportSelected}>
                Export selected
              </Button>
            </div>
          ) : null}

          <div className="hidden md:block overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={(v) => toggleSelectAll(v === true)}
                      aria-label="Select all entries on this page"
                    />
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Batch IDs</TableHead>
                  <TableHead>Dealer</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <div className="flex flex-col gap-2 py-4">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-[75%]" />
                        <Skeleton className="h-4 w-1/2" />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-muted-foreground">
                      No entries yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((e) => (
                    <Fragment key={e.id}>
                      <TableRow>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(String(e.id))}
                            onCheckedChange={(v) => toggleRow(String(e.id), v === true)}
                            aria-label={`Select entry ${e.id}`}
                          />
                        </TableCell>
                        <TableCell className="w-10 align-middle">
                          <button
                            type="button"
                            onClick={() => void toggleExpand(e.id)}
                            className="flex h-9 w-9 min-h-9 min-w-9 items-center justify-center rounded-md hover:bg-accent text-muted-foreground [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
                            aria-label={expandedId === e.id ? "Collapse details" : "Expand details"}
                          >
                            {expandedId === e.id ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{e.id}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {e.batch_ids?.length ? e.batch_ids.join(", ") : "-"}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {dealerNameById.get(String(e.dealer_id)) ?? e.dealer_id}
                        </TableCell>
                        <TableCell>{e.reference_no}</TableCell>
                        <TableCell>{e.total_amount}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {e.created_at ? new Date(e.created_at).toLocaleString() : "-"}
                        </TableCell>
                      </TableRow>
                      {expandedId === e.id ? (
                        <TableRow>
                          <TableCell colSpan={8} className="bg-muted/30">
                            <div className="space-y-2 text-sm">
                              {loadingDetailId === e.id ? (
                                <div className="text-muted-foreground">Loading details...</div>
                              ) : detailError ? (
                                <div className="text-destructive">{detailError}</div>
                              ) : null}
                              {details[e.id] ? (
                                <div className="space-y-2">
                                  <div className="font-medium">Items</div>
                                  <div className="grid gap-2">
                                    {details[e.id]!.items.map((it, idx) => (
                                      <div
                                        key={`${it.product_id}-${idx}`}
                                        className="grid grid-cols-2 md:grid-cols-4 gap-2 rounded border px-3 py-2 bg-background"
                                      >
                                        <div>
                                          <div className="text-xs text-muted-foreground">Product</div>
                                          <div className="font-mono text-xs">
                                            {productNameById.get(String(it.product_id)) ?? it.product_id}
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-muted-foreground">Quantity</div>
                                          <div>{it.quantity}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-muted-foreground">Purchase</div>
                                          <div>{it.purchase_price}</div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-muted-foreground">Selling / MRP</div>
                                          <div>
                                            {it.selling_price} / {it.mrp}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="md:hidden space-y-3">
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-28 w-full rounded-xl" />
                ))}
              </div>
            ) : entries.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">No entries yet.</div>
            ) : (
              entries.map((e) => (
                <Card key={e.id} className="border-border">
                  <CardContent className="space-y-3 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <Checkbox
                        checked={selectedIds.has(String(e.id))}
                        onCheckedChange={(v) => toggleRow(String(e.id), v === true)}
                        aria-label={`Select entry ${e.id}`}
                      />
                      <Button type="button" size="sm" variant="outline" onClick={() => void openMobileDrawer(String(e.id))}>
                        Details
                      </Button>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Dealer</span>
                        <span className="font-medium text-right truncate">
                          {dealerNameById.get(String(e.dealer_id)) ?? e.dealer_id}
                        </span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Reference</span>
                        <span className="text-right">{e.reference_no}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Total</span>
                        <span className="font-semibold text-right">{e.total_amount}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground">Created</span>
                        <span className="text-right text-xs">
                          {e.created_at ? new Date(e.created_at).toLocaleString() : "-"}
                        </span>
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">ID {e.id}</div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {totalPages > 1 ? (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={pageClamped <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {pageClamped} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={pageClamped >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Drawer open={mobileDrawerEntryId != null} onOpenChange={(o) => !o && setMobileDrawerEntryId(null)}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader>
            <DrawerTitle>Entry details</DrawerTitle>
            <DrawerDescription>
              {mobileDrawerEntry ? `ID ${mobileDrawerEntry.id}` : ""}
            </DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-4">
            {mobileDrawerEntry && loadingDetailId === mobileDrawerEntry.id ? (
              <div className="text-sm text-muted-foreground">Loading details...</div>
            ) : null}
            {detailError ? <div className="text-sm text-destructive">{detailError}</div> : null}
            {mobileDrawerEntryId && details[mobileDrawerEntryId] ? (
              <div className="space-y-2 text-sm">
                <div className="font-medium">Items</div>
                <div className="grid gap-2">
                  {details[mobileDrawerEntryId]!.items.map((it, idx) => (
                    <div
                      key={`${it.product_id}-${idx}`}
                      className="grid grid-cols-2 gap-2 rounded border px-3 py-2 bg-muted/30"
                    >
                      <div>
                        <div className="text-xs text-muted-foreground">Product</div>
                        <div className="font-mono text-xs">
                          {productNameById.get(String(it.product_id)) ?? it.product_id}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Quantity</div>
                        <div>{it.quantity}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Purchase</div>
                        <div>{it.purchase_price}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Selling / MRP</div>
                        <div>
                          {it.selling_price} / {it.mrp}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button type="button" variant="secondary">
                Close
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {isMobile && selectedCount > 0 ? (
        <BottomActionBar>
          <span className="text-sm font-medium">{selectedCount} selected</span>
          <Button type="button" size="sm" variant="outline" onClick={clearSelection}>
            Clear
          </Button>
          <Button type="button" size="sm" onClick={exportSelected}>
            Export
          </Button>
        </BottomActionBar>
      ) : null}
    </div>
  );
}
